import type { NodeDefinition } from '../../../types/node-definition';

/**
 * Knowledge Base Search Node
 *
 * Performs semantic search across a knowledge base collection.
 * Can be used as a standalone node or as a tool for AI Agent.
 * Uses the product-wide OpenAI API key for embeddings.
 *
 * Parameters:
 * - collectionId: The knowledge base collection to search
 * - query: The search query
 * - topK: Number of results to return
 * - minScore: Minimum similarity score threshold
 * - includeContent: Whether to include full document content
 * - outputFormat: How to format results (full, context, documents)
 */
export const kbSearchDefinition: NodeDefinition = {
  type: 'kbSearch',
  name: 'KB Search',
  category: 'utilities',
  description: 'Perform semantic search across a knowledge base collection',
  icon: 'Search',
  color: 'purple',
  version: '1.0.0',

  group: ['ai', 'knowledge-base'],

  inputs: [
    {
      name: 'main',
      type: 'main',
      displayName: 'Main',
      required: false
    }
  ],

  outputs: [
    {
      name: 'main',
      type: 'main',
      displayName: 'Main'
    }
  ],

  // Tool-capable configuration for AI Agent
  toolCapable: {
    enabled: true,
    name: 'kb_search',
    description: 'Search the knowledge base for relevant information using semantic search',
    allowManualDescription: true,
    parameters: {
      query: {
        toolParamName: 'query',
        description: 'The search query to find relevant documents',
        required: true,
        type: 'string',
      },
      topK: {
        toolParamName: 'topK',
        description: 'Number of results to return (default: 5)',
        required: false,
        type: 'number',
      },
      minScore: {
        toolParamName: 'minScore',
        description: 'Minimum similarity score threshold (0-1, default: 0.7)',
        required: false,
        type: 'number',
      },
    },
    outputMapping: {
      results: 'Array of matching document chunks with content and scores',
      totalResults: 'Number of results found',
      query: 'The search query used',
    },
  },

  defaults: {
    name: 'KB Search',
    color: 'purple'
  }
};
